import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Box,
  Autocomplete,
  Divider,
  Chip
} from '@mui/material';
import {
  LabelStyle,
  TextFieldStyle,
  selectStyle,
  errorTextStyles,
  autocompleteStyles,
  visibleChipStyles
} from '../styles/CandidateForm.styles';
import { TYPE_OPTIONS, normalizeEmploymentType } from '../constants/formOptions';
import { getAllRoles } from 'src/services/RoleManagementService';
import {
  getDesignationOptions,
  getReportingManagers
} from 'src/services/employeeService';
import { DatePicker } from '@mui/lab';
import WorkIcon from '@mui/icons-material/Work';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

interface EmploymentDetailsProps {
  thirdPageValidation: any;
  setThirdPageValidation: (value: any) => void;
  thirdErrors: any;
  setThirdErrors: (value: any) => void;
  selectedDesignation: any;
  setSelectedDesignation: (value: any) => void;
  selectedManager: any;
  setSelectedManager: (value: any) => void;
  selectedUserRole: any[];
  setSelectedUserRole: (value: any[]) => void;
  selectedType: string;
  setSelectedType: (value: string) => void;
  isViewMode: boolean;
  isEditMode?: boolean;
  isCreateEmsUserMode?: boolean;
  register: any;
}

const EmploymentDetails: React.FC<EmploymentDetailsProps> = ({
  thirdPageValidation,
  setThirdPageValidation,
  thirdErrors,
  setThirdErrors,
  selectedDesignation,
  setSelectedDesignation,
  selectedManager,
  setSelectedManager,
  selectedUserRole,
  setSelectedUserRole,
  selectedType,
  setSelectedType,
  isViewMode,
  isEditMode = false,
  isCreateEmsUserMode = false,
  register
}) => {
  const shouldDisableDesignation = isViewMode || isEditMode;
  const shouldDisableDepartmentAndTier = true;
  const [userRoles, setUserRoles] = useState<any[]>([]);
  const [designationOptions, setDesignationOptions] = useState<any[]>([]);
  const [reportingManagers, setReportingManagers] = useState<any[]>([]);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [showAllRoleOptions, setShowAllRoleOptions] = useState(false);

  const maxVisibleOptions = 3;
  const visibleRoleOptions = showAllRoleOptions
    ? userRoles
    : userRoles.slice(0, maxVisibleOptions);

  const dropdownOptions =
    userRoles.length > maxVisibleOptions && !showAllRoleOptions
      ? [
          ...visibleRoleOptions,
          {
            label: showAllRoleOptions ? 'See Less' : 'See More',
            value: '__toggle__'
          }
        ]
      : userRoles;

  const minAllowedDate = new Date();
  minAllowedDate.setFullYear(minAllowedDate.getFullYear() - 1);
  minAllowedDate.setHours(0, 0, 0, 0);

  const parseDateValue = (value?: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;

    const parsedDate = new Date(year, month - 1, day);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const formatDateValue = (value: Date | null): string => {
    if (!value || Number.isNaN(value.getTime())) return '';

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Helper function to check if field is required
  const isFieldRequired = (fieldName: string): boolean => {
    if (isCreateEmsUserMode) {
      // All fields are mandatory in create EMS user mode
      return true;
    }
    // Default required fields
    return ['designation', 'department', 'joinDate'].includes(fieldName);
  };

  // Helper function to check if a role is already selected
  const isRoleSelected = (role: any) => {
    return selectedUserRole?.some(
      (selected) =>
        selected.value === role.value || selected.label === role.label
    );
  };

  const hasRemovableSelectedRole = selectedUserRole?.some(
    (role) => role.label !== 'General User'
  );

  const getUserRoles = async () => {
    try {
      const roles = await getAllRoles();
      const mappedRoles = (roles?.data || []).map((role: any) => ({
        value: role.roleId,
        label: role.name,
        original: role
      }));
      setUserRoles(mappedRoles);
    } catch (error) {
      setUserRoles([]);
    }
  };

  useEffect(() => {
    // Skip default role setting in edit/view/createEmsUser modes as roles are passed from parent
    if (isEditMode || isViewMode || isCreateEmsUserMode) {
      return;
    }

    // Ensure General User is selected by default only for create mode
    if (
      userRoles &&
      userRoles.length > 0 &&
      (!selectedUserRole || selectedUserRole.length === 0)
    ) {
      const generalUser = userRoles.find((r) => r?.label === 'General User');
      if (generalUser) {
        setSelectedUserRole([generalUser]);
        setThirdPageValidation((prev: any) => ({
          ...prev,
          userRoleId: generalUser.value
        }));
      }
    }
  }, [
    userRoles,
    selectedUserRole,
    setSelectedUserRole,
    setThirdPageValidation,
    isEditMode,
    isViewMode,
    isCreateEmsUserMode
  ]);

  const fetchDesignationOptions = async () => {
    try {
      const designations = await getDesignationOptions();
      setDesignationOptions(designations);
    } catch (error) {
      setDesignationOptions([]);
    }
  };

  const fetchReportingManagers = async () => {
    try {
      const managers = await getReportingManagers();
      setReportingManagers(managers);
    } catch (error) {
      setReportingManagers([]);
    }
  };

  useEffect(() => {
    getUserRoles();
    fetchDesignationOptions();
    fetchReportingManagers();
  }, []);

  useEffect(() => {
    if (!selectedManager || reportingManagers.length === 0) return;

    const selectedName = (
      selectedManager.label ||
      selectedManager.name ||
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
    const selectedId =
      selectedManager.employeeId ||
      selectedManager.id ||
      selectedManager.value;

    const matched = reportingManagers.find((manager) => {
      const managerName = (manager.label || manager.name || '')
        .toString()
        .trim()
        .toLowerCase();
      const managerId = manager.employeeId || manager.id || manager.value;

      const nameMatches = selectedName && managerName === selectedName;
      const idMatches =
        selectedId &&
        managerId &&
        String(managerId) === String(selectedId);

      return nameMatches || idMatches;
    });

    if (!matched) return;

    const alreadyMatched =
      matched.id === selectedManager.id &&
      matched.value === selectedManager.value &&
      matched.label === selectedManager.label;

    if (!alreadyMatched) {
      setSelectedManager(matched);
      setThirdPageValidation((prev: any) => ({
        ...prev,
        reportingManagerId: matched.id || matched.value || ''
      }));
    }
  }, [reportingManagers, selectedManager, setSelectedManager, setThirdPageValidation]);

  useEffect(() => {
    if (!selectedType) return;

    setThirdPageValidation((prev: any) => ({
      ...prev,
      type: prev?.type || selectedType
    }));

    if (thirdErrors?.employmentTypeError) {
      setThirdErrors((prev: any) => ({
        ...prev,
        employmentTypeError: ''
      }));
    }
  }, [
    selectedType,
    thirdErrors?.employmentTypeError,
    setThirdErrors,
    setThirdPageValidation
  ]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 1.5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: '#e3f2fd',
              color: '#1565c0'
            }}
          >
            <WorkIcon fontSize="small" />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#1565c0',
              fontSize: '1.1rem'
            }}
          >
            Employment Details
          </Typography>
        </Box>
        <Divider
          sx={{
            flex: 1,
            borderColor: '#1565c0',
            borderWidth: '0.5px'
          }}
        />
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Employee ID{' '}
            {isFieldRequired('employeeId') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>
          <TextField
            size="small"
            {...register('employeeId')}
            sx={TextFieldStyle}
            fullWidth
            placeholder="Enter Employee ID"
            variant="outlined"
            disabled={isViewMode}
            value={thirdPageValidation.employeeId || ''}
            onChange={(e) => {
              setThirdPageValidation((prev: any) => ({
                ...prev,
                employeeId: e.target.value
              }));
              if (thirdErrors?.employeeIdError) {
                setThirdErrors((prev: any) => ({
                  ...prev,
                  employeeIdError: ''
                }));
              }
            }}
          />
          {thirdErrors?.employeeIdError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.employeeIdError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Designation{' '}
            {isFieldRequired('designation') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>
          <Autocomplete
            size="small"
            options={designationOptions}
            getOptionLabel={(option) =>
              option.label || option.name || option.designationName || ''
            }
            value={selectedDesignation}
            disabled={shouldDisableDesignation}
            loading={designationOptions.length === 0}
            onChange={(event, newValue) => {
              setSelectedDesignation(newValue);

              // Auto-fill department and tier based on selected designation
              if (newValue) {
                setThirdPageValidation((prev: any) => ({
                  ...prev,
                  department: newValue.value || '',
                  tier: newValue.tier || ''
                }));
              } else {
                // Clear department and tier if no designation is selected
                setThirdPageValidation((prev: any) => ({
                  ...prev,
                  department: '',
                  tier: ''
                }));
              }

              if (thirdErrors?.designationError) {
                setThirdErrors((prev: any) => ({
                  ...prev,
                  designationError: ''
                }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  ...autocompleteStyles,
                  '& .MuiAutocomplete-endAdornment': {
                    right: { xs: '6px', sm: '8px' },
                    '& .MuiAutocomplete-clearIndicator': {
                      padding: { xs: '2px', sm: '3px' },
                      margin: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: '14px', sm: '16px' }
                      }
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                      padding: { xs: '2px', sm: '3px' },
                      margin: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: '16px', sm: '18px' }
                      }
                    }
                  },
                  ...(shouldDisableDesignation && {
                    '& .MuiInputBase-root': {
                      ...autocompleteStyles['& .MuiInputBase-root'],
                      backgroundColor: '#f9f9f9',
                      cursor: 'not-allowed'
                    }
                  })
                }}
                placeholder="Search and Select Designation"
                variant="outlined"
              />
            )}
          />
          {thirdErrors?.designationError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.designationError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Department{' '}
            {isFieldRequired('department') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>
          <TextField
            size="small"
            sx={{
              ...TextFieldStyle,
              '& .MuiInputBase-root': {
                ...TextFieldStyle['& .MuiInputBase-root'],
                backgroundColor: '#f9f9f9',
                cursor: 'not-allowed'
              }
            }}
            fullWidth
            placeholder="Auto-filled based on designation"
            variant="outlined"
            disabled={shouldDisableDepartmentAndTier}
            value={thirdPageValidation.department || ''}
          />
          {thirdErrors?.departmentError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.departmentError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Join Date{' '}
            {isFieldRequired('joinDate') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>
          <DatePicker
            inputFormat="yyyy-MM-dd"
            disabled={isViewMode}
            minDate={minAllowedDate}
            value={parseDateValue(thirdPageValidation.joinDate)}
            onChange={(newValue) => {
              setThirdPageValidation((prev: any) => ({
                ...prev,
                joinDate: formatDateValue(newValue as Date | null)
              }));
              if (thirdErrors?.joinDateError) {
                setThirdErrors((prev: any) => ({
                  ...prev,
                  joinDateError: ''
                }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={TextFieldStyle}
                fullWidth
                variant="outlined"
                placeholder="Select Join Date"
              />
            )}
          />
          {thirdErrors?.joinDateError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.joinDateError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Tier{' '}
            {isFieldRequired('tier') && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            sx={{
              ...TextFieldStyle,
              '& .MuiInputBase-root': {
                ...TextFieldStyle['& .MuiInputBase-root'],
                backgroundColor: '#f9f9f9',
                cursor: 'not-allowed'
              }
            }}
            fullWidth
            placeholder="Auto-filled based on designation"
            variant="outlined"
            disabled={shouldDisableDepartmentAndTier}
            value={thirdPageValidation.tier || ''}
          />
          {thirdErrors?.tierError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.tierError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Primary Reporting Manager{' '}
            {isFieldRequired('reportingManager') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>
          <Autocomplete
            size="small"
            options={reportingManagers}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (!option) return '';
              return option.label || option.name || '';
            }}
            value={selectedManager}
            isOptionEqualToValue={(option, value) =>
              option?.id === value?.id ||
              option?.value === value?.value ||
              (option?.label || '').toLowerCase() ===
                (value?.label || '').toLowerCase()
            }
            disabled={isViewMode}
            loading={reportingManagers.length === 0}
            filterSelectedOptions
            autoHighlight
            clearOnBlur={false}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const searchTerm = inputValue.trim().toLowerCase();
              return options.filter((option) => {
                const label = (option.label || '').trim().toLowerCase();
                const name = (option.name || '').trim().toLowerCase();
                return label.includes(searchTerm) || name.includes(searchTerm);
              });
            }}
            onChange={(event, newValue) => {
              setSelectedManager(newValue);
              setThirdPageValidation((prev: any) => ({
                ...prev,
                reportingManagerId:
                  newValue?.id || newValue?.value || newValue?.employeeId || ''
              }));
              if (thirdErrors?.reportingManagerError) {
                setThirdErrors((prev: any) => ({
                  ...prev,
                  reportingManagerError: ''
                }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={autocompleteStyles}
                placeholder="Search and Select Primary Reporting Manager"
                variant="outlined"
              />
            )}
            noOptionsText="No managers found"
            loadingText="Loading managers..."
          />
          {thirdErrors?.reportingManagerError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.reportingManagerError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Employment Type{' '}
            {isFieldRequired('employmentType') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>
          <FormControl fullWidth>
            <Select
              size="small"
              sx={selectStyle}
              displayEmpty
              value={normalizeEmploymentType(selectedType)}
              disabled={isViewMode}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setThirdPageValidation((prev: any) => ({
                  ...prev,
                  type: e.target.value
                }));
                if (thirdErrors?.employmentTypeError) {
                  setThirdErrors((prev: any) => ({
                    ...prev,
                    employmentTypeError: ''
                  }));
                }
              }}
              renderValue={(selected) =>
                selected ? (
                  TYPE_OPTIONS.find((opt) => opt.value === selected)?.label
                ) : (
                  <span style={{ color: '#aaa' }}>Select Employment Type</span>
                )
              }
            >
              <MenuItem value="" disabled>
                Select Employment Type
              </MenuItem>
              {TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {thirdErrors?.employmentTypeError && !selectedType && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.employmentTypeError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            From Date{' '}
            {isFieldRequired('fromDate') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>
          <DatePicker
            inputFormat="yyyy-MM-dd"
            disabled={isViewMode}
            minDate={minAllowedDate}
            value={parseDateValue(thirdPageValidation.fromDate)}
            onChange={(newValue) => {
              setThirdPageValidation((prev: any) => ({
                ...prev,
                fromDate: formatDateValue(newValue as Date | null)
              }));
              if (thirdErrors?.fromDateError) {
                setThirdErrors((prev: any) => ({
                  ...prev,
                  fromDateError: ''
                }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...register('fromDate')}
                size="small"
                sx={TextFieldStyle}
                fullWidth
                variant="outlined"
                placeholder="Select From Date"
              />
            )}
          />
          {thirdErrors?.fromDateError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.fromDateError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            User Role{' '}
            {isFieldRequired('userRole') && (
              <span style={{ color: 'red' }}>*</span>
            )}
          </Typography>

          {/* Selected Roles Display */}
          {selectedUserRole && selectedUserRole.length > 0 && (
            <Box
              sx={{
                mb: 1,
                p: { xs: 0.75, sm: 1, md: 1 },
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#fafafa',
                maxHeight: showAllRoles
                  ? 'none'
                  : { xs: '70px', sm: '80px', md: '90px' },
                minHeight: { xs: '40px', sm: '50px', md: '55px' },
                overflowY: showAllRoles ? 'visible' : 'auto',
                transition: 'max-height 0.3s ease'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: { xs: 0.5, sm: 0.75, md: 1 }
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#666',
                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
                  }}
                >
                  {`Selected Role${selectedUserRole.length > 1 ? 's' : ''} (${
                    selectedUserRole.length
                  })`}
                </Typography>
                {selectedUserRole.length > 3 && (
                  <Typography
                    sx={{
                      color: '#1976d2',
                      cursor: 'pointer',
                      fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => setShowAllRoles(!showAllRoles)}
                  >
                    {showAllRoles ? 'Show Less' : 'Show All'}
                    {showAllRoles ? (
                      <ExpandLess
                        sx={{
                          fontSize: { xs: '14px', sm: '16px', md: '18px' }
                        }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{
                          fontSize: { xs: '14px', sm: '16px', md: '18px' }
                        }}
                      />
                    )}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 0.375, sm: 0.5, md: 0.5 },
                  alignItems: 'flex-start'
                }}
              >
                {(showAllRoles
                  ? selectedUserRole
                  : selectedUserRole.slice(0, 3)
                ).map((role) => {
                  const isGeneralUser = role.label === 'General User';
                  const canRemoveRole =
                    !isViewMode && !isGeneralUser && hasRemovableSelectedRole;

                  return (
                    <Chip
                      key={role.value}
                      label={role.label}
                      size="small"
                      sx={{
                        ...visibleChipStyles,
                        backgroundColor: isGeneralUser ? '#f3e5f5' : '#e3f2fd',
                        color: isGeneralUser ? '#7b1fa2' : '#1565c0',
                        border: isGeneralUser
                          ? '1px solid #ce93d8'
                          : '1px solid #90caf9',
                        '& .MuiChip-deleteIcon': {
                          fontSize: {
                            xs: '13px',
                            sm: '14px',
                            md: '15px'
                          },
                          marginRight: '2px'
                        }
                      }}
                      onDelete={
                        canRemoveRole
                          ? () => {
                              const newRoles = selectedUserRole.filter(
                                (r) => r.value !== role.value
                              );
                              setSelectedUserRole(newRoles);
                              setThirdPageValidation((prev: any) => ({
                                ...prev,
                                userRoleId: newRoles
                                  .map((r) => r.value)
                                  .join(',')
                              }));
                            }
                          : undefined
                      }
                    />
                  );
                })}
                {!showAllRoles && selectedUserRole.length > 3 && (
                  <Chip
                    label={`+${selectedUserRole.length - 3} more`}
                    size="small"
                    sx={{
                      ...visibleChipStyles,
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      border: '1px solid #ddd',
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowAllRoles(true)}
                  />
                )}
              </Box>
            </Box>
          )}

          <Autocomplete
            multiple
            size="small"
            options={dropdownOptions}
            disableCloseOnSelect
            getOptionLabel={(option) => option?.label || ''}
            value={selectedUserRole || []}
            disabled={isViewMode}
            disableClearable={!hasRemovableSelectedRole || isViewMode}
            loading={userRoles?.length === 0}
            onChange={(event, newValue, reason, details) => {
              // Handle See More/See Less click
              if (details?.option?.value === '__toggle__') {
                setShowAllRoleOptions((prev) => !prev);
                return;
              }

              // Ensure General User is always included and can't be removed
              const generalUser = userRoles?.find(
                (r) => r.label === 'General User'
              );
              let updatedRoles = [...(newValue || [])];

              if (
                generalUser &&
                !updatedRoles.some((r) => r.label === 'General User')
              ) {
                updatedRoles.unshift(generalUser);
              }

              setSelectedUserRole(updatedRoles);
              setThirdPageValidation((prev: any) => ({
                ...prev,
                userRoleId: updatedRoles.map((r) => r.value).join(',')
              }));

              if (thirdErrors?.userRoleError) {
                setThirdErrors((prev: any) => ({
                  ...prev,
                  userRoleError: ''
                }));
              }
            }}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  ...TextFieldStyle,
                  '& .MuiInputBase-root': {
                    ...TextFieldStyle['& .MuiInputBase-root'],
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '40px !important'
                  },
                  '& .MuiInputBase-input': {
                    ...TextFieldStyle['& .MuiInputBase-input'],
                    flex: 1
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    right: { xs: '6px', sm: '8px', md: '10px' },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: 'auto',
                    '& .MuiAutocomplete-clearIndicator': {
                      padding: { xs: '2px', sm: '3px', md: '4px' },
                      margin: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: '14px', sm: '15px', md: '16px' }
                      }
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                      padding: { xs: '2px', sm: '3px', md: '4px' },
                      margin: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: '16px', sm: '18px', md: '20px' }
                      }
                    }
                  }
                }}
                placeholder={
                  selectedUserRole?.length > 0
                    ? 'Search for more roles...'
                    : 'Search and Select User Role'
                }
                variant="outlined"
              />
            )}
            renderOption={(props, option) => {
              if (option.value === '__toggle__') {
                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      fontWeight: 600,
                      color: '#1976d2',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                      py: 1
                    }}
                  >
                    {showAllRoleOptions ? 'See Less' : 'See More'}
                  </Box>
                );
              }

              const isSelected = isRoleSelected(option);
              const isGeneralUser = option.label === 'General User';
              const isDisabled = isSelected;

              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    padding: '8px 12px',
                    backgroundColor: isSelected ? '#f0f8ff' : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? '#f0f8ff' : '#f5f5f5'
                    },
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    pointerEvents: isDisabled ? 'none' : 'auto'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%'
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: 'inherit',
                        fontWeight: isGeneralUser ? 600 : 400,
                        color: isDisabled ? '#999' : 'inherit'
                      }}
                    >
                      {option.label}
                      {isGeneralUser && ' (Default)'}
                      {isSelected && ' (Already Selected)'}
                    </Typography>
                    {isSelected && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#4caf50'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            }}
            getOptionDisabled={(option) =>
              option.value !== '__toggle__' && isRoleSelected(option)
            }
            ListboxProps={{
              style: {
                maxHeight: '250px'
              }
            }}
          />
          {thirdErrors?.userRoleError && (
            <Typography sx={errorTextStyles} color="red">
              {thirdErrors?.userRoleError}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default EmploymentDetails;
